'use no memo';

import type { UserPrivate } from '@repo/types/users';
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table';

import { useGetParents } from '@repo/api/parent/useGetParents';
import { useGetManagedUsers } from '@repo/api/user/useGetManagedUsers';
import { AccountType } from '@repo/types/enums/accountType';
import { UserLvl } from '@repo/types/enums/userLvl';
import { endOfDay, format, isValid, parseISO, startOfDay } from 'date-fns';
import { EditIcon, ListPlusIcon, PlusIcon, Trash2Icon, UserIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import ActivateUser from '@/components/features/manage/manage-users/ActivateUser';
import CreateUser from '@/components/features/manage/manage-users/CreateUser';
import DeleteUserDialog from '@/components/features/manage/manage-users/DeleteUser';
import EditUser from '@/components/features/manage/manage-users/EditUser';
import GenerateStudentReport from '@/components/features/manage/manage-users/GenerateStudentReport';
import { getInviteStatus } from '@/components/features/manage/manage-users/inviteStatus';
import ResendInvite from '@/components/features/manage/manage-users/ResendInvite';
import ResetPassword from '@/components/features/manage/manage-users/ResetPassword';
import TanstackTable from '@/components/features/manage/tanstack-table/TanstackTable';
import { useTanstackTable } from '@/components/features/manage/tanstack-table/useTanstackTable';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useMediaQuery } from '@/hooks/media-query';
import { useTableRowSelection } from '@/hooks/table';
import { getAvatar } from '@/utils/getAvatar';

export default function ManageUsers() {
  const breakpoint = useMediaQuery('md');

  const navigate = useNavigate();

  const [openResetPassword, setOpenResetPassword] = useState<boolean>(false);
  const [openChangeActive, setOpenChangeActive] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [openUserAdd, setOpenUserAdd] = useState<boolean>(false);
  const [openReport, setOpenReport] = useState<boolean>(false);
  const [openResendInvite, setOpenResendInvite] = useState<boolean>(false);

  const [activeUser, setActiveUser] = useState<UserPrivate | undefined>(undefined);

  const [rowSelection, setRowsSelection] = useState<RowSelectionState>({});

  const { data: managedUsers = [], isPending } = useGetManagedUsers();
  const { data: parents = [] } = useGetParents();

  const students = useMemo(() => managedUsers.filter((user) => user.lvl === UserLvl.STUDENT && (user.accountType === undefined || user.accountType === AccountType.User)), [managedUsers]);

  const columns = useMemo<ColumnDef<UserPrivate>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected() ? true : table.getIsSomePageRowsSelected() ? 'indeterminate' : false}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label={'Select row'} />,
        enableSorting: false,
        enableHiding: false,
      },
      {
        meta: 'Awatar',
        id: 'avatarURL',
        accessorKey: 'avatarURL',
        cell: ({ getValue }) => {
          const avatarURL = getValue();
          const url = typeof avatarURL === 'string' ? getAvatar(avatarURL) : undefined;
          return (
            <Avatar size={'sm'}>
              <AvatarImage src={url} />
              <AvatarFallback>
                <UserIcon className="size-3.5" />
              </AvatarFallback>
            </Avatar>
          );
        },
        enableSorting: false,
        header: 'Awatar',
      },
      {
        meta: 'Imię',
        id: 'name',
        accessorKey: 'name',
        header: 'Imię',
      },
      {
        meta: 'Nazwisko',
        id: 'surname',
        accessorKey: 'surname',
        header: 'Nazwisko',
      },
      {
        meta: 'Email',
        id: 'email',
        accessorKey: 'email',
        header: 'Email',
        cell: ({ getValue }) => {
          const email = getValue() as UserPrivate['email'];
          if (!email) {
            return <p>-</p>;
          }
          return <a href={`mailto:${email}`}>{email}</a>;
        },
      },
      {
        meta: 'Stanowisko',
        id: 'lvl',
        accessorKey: 'lvl',

        cell: ({ getValue }) => {
          const lvl = getValue();
          if (lvl === 0) {
            return <Badge variant={'blue'}>Uczeń</Badge>;
          }
          if (lvl === 1) {
            return <Badge variant={'turquoise'}>Nauczyciel</Badge>;
          }
          if (lvl === 2) {
            return <Badge variant={'purple'}>Administrator</Badge>;
          }
        },
        header: 'Stanowisko',
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        meta: 'Nr.tel',
        id: 'phone',
        accessorKey: 'phone',
        header: ' Nr.tel',
        cell: ({ getValue }) => {
          const phone = getValue() as UserPrivate['phone'];
          if (!phone) {
            return <p>-</p>;
          }
          return <a href={`tel:${phone}`}>{phone}</a>;
        },
      },
      {
        meta: 'Data ur.',
        id: 'birthdate',
        accessorKey: 'birthdate',
        sortType: 'datetime',
        cell: ({ getValue }) => {
          const date = getValue();
          if (typeof date === 'string' && isValid(parseISO(date))) {
            return format(new Date(date), 'dd.MM.yyyy');
          }

          if (!date) {
            return <p>-</p>;
          }

          return date;
        },
        header: 'Data ur.',
        filterFn: (row, id, value) => {
          if (!value || (!value.start && !value.end)) return true;
          const dateStr = row.getValue(id) as string;
          if (!dateStr || !isValid(parseISO(dateStr))) return false;
          const date = startOfDay(parseISO(dateStr));
          const start = value.start ? startOfDay(new Date(value.start)) : null;
          const end = value.end ? endOfDay(new Date(value.end)) : null;
          if (start && end) {
            return date >= start && date <= end;
          }
          if (start) {
            return date >= start;
          }
          if (end) {
            return date <= end;
          }
          return true;
        },
      },
      {
        meta: 'Utworzono',
        id: 'createdAt',
        accessorKey: 'createdAt',
        sortType: 'datetime',
        cell: ({ getValue }) => {
          const date = getValue();
          if (typeof date === 'string' && isValid(parseISO(date))) {
            return format(new Date(date), 'dd.MM.yyyy');
          }
          return date;
        },
        header: 'Utworzono',
        filterFn: (row, id, value) => {
          if (!value || (!value.start && !value.end)) return true;
          const dateStr = row.getValue(id) as string;
          if (!dateStr || !isValid(parseISO(dateStr))) return false;
          const date = startOfDay(parseISO(dateStr));
          const start = value.start ? startOfDay(new Date(value.start)) : null;
          const end = value.end ? endOfDay(new Date(value.end)) : null;
          if (start && end) {
            return date >= start && date <= end;
          }
          if (start) {
            return date >= start;
          }
          if (end) {
            return date <= end;
          }
          return true;
        },
      },
      {
        meta: 'Rejestracja',
        id: 'last_login',
        accessorKey: 'last_login',
        cell: ({ getValue }) => {
          const last_login = getValue();
          if (last_login) {
            return <Badge variant={'success'}>Zarejestrowany</Badge>;
          } else {
            return <Badge variant={'destructive'}>Niezarejestrowany</Badge>;
          }
        },
        header: 'Rejestracja',
        filterFn: (row, id, value) => {
          if (!value || !Array.isArray(value) || value.length === 0) return true;
          const lastLogin = row.getValue(id);
          return value.includes(!!lastLogin);
        },
      },
      {
        meta: 'Zaproszenie',
        id: 'authTokenExpiry',
        accessorKey: 'authTokenExpiry',
        enableSorting: false,
        cell: ({ row }) => {
          const invite = getInviteStatus(row.original);
          switch (invite.state) {
            case 'signed-up':
              return <Badge variant={'success'}>Zaakceptowano</Badge>;
            case 'valid':
              return <Badge variant={'warning'}>{`Wysłano ${format(invite.sentAt, 'dd.MM.yyyy HH:mm')}, ważne do ${format(invite.expiresAt, 'dd.MM.yyyy HH:mm')}`}</Badge>;
            case 'expired':
              return <Badge variant={'destructive'}>Wygasło {format(invite.expiresAt, 'dd.MM.yyyy HH:mm')}</Badge>;
            case 'none':
              return <Badge variant={'muted'}>Nie wysłano</Badge>;
          }
        },
        header: 'Zaproszenie',
      },
      {
        meta: 'Aktywność',
        id: 'active',
        accessorKey: 'active',
        cell: ({ getValue }) => {
          const active = getValue();

          if (active === true) {
            return <Badge variant={'success'}>Aktywny</Badge>;
          }
          if (active === false) {
            return <Badge variant={'destructive'}>Nieaktywny</Badge>;
          }
          return '';
        },
        header: 'Aktywność',
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        id: 'actions',
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          const rowOrigin = row.original;
          return (
            <div className={'flex w-full justify-end'}>
              <Button
                size={'sm'}
                variant={'ghost'}
                variantColor={'destructive'}
                onClick={() => {
                  setActiveUser(rowOrigin);
                  setOpenDelete(true);
                }}
              >
                <Trash2Icon />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size={'sm'} variant={'ghost'}>
                    <EditIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={'end'} className="w-full max-w-xs">
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      onClick={() => {
                        setActiveUser(rowOrigin);
                        setOpenEdit(true);
                      }}
                    >
                      <div className="space-y-0.5">
                        <p>Edytuj</p>
                        <p className="text-muted-foreground ms-1 text-xs">Edytuj dane użytkownika</p>
                      </div>
                    </DropdownMenuItem>
                    {/* The BE rejects anything that isn't a student account,
                        so don't offer the action where it would 403. */}
                    {rowOrigin.lvl === UserLvl.STUDENT && rowOrigin.accountType === AccountType.User ? (
                      <DropdownMenuItem
                        onClick={() => {
                          setActiveUser(rowOrigin);
                          setOpenReport(true);
                        }}
                      >
                        <div className="space-y-0.5">
                          <p>Generuj raport</p>
                          <p className="text-muted-foreground ms-1 text-xs">Pobierz PDF z ocenami i frekwencją, opcjonalnie wyślij go rodzicom</p>
                        </div>
                      </DropdownMenuItem>
                    ) : null}
                    {/* Only users who never completed signup have an invite to resend;
                        the BE also rejects deactivated accounts. */}
                    {!rowOrigin.last_login && rowOrigin.active ? (
                      <DropdownMenuItem
                        onClick={() => {
                          setActiveUser(rowOrigin);
                          setOpenResendInvite(true);
                        }}
                      >
                        <div className="space-y-0.5">
                          <p>Wyślij zaproszenie</p>
                          <p className="text-muted-foreground ms-1 text-xs">Wyślij ponownie link rejestracyjny, poprzedni link przestanie działać</p>
                        </div>
                      </DropdownMenuItem>
                    ) : null}
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={() => {
                        setActiveUser(rowOrigin);
                        setOpenChangeActive(true);
                      }}
                    >
                      {rowOrigin.active ? (
                        <div className="space-y-0.5">
                          <p>Dezaktywuj</p>
                          <p className="text-text-tertiary ms-1 text-xs">Dezaktywuj konto użytkownika, użytkownik nie będzie miał dostępu do konta</p>
                        </div>
                      ) : (
                        <div className="space-y-0.5">
                          <p>Aktywuj</p>
                          <p className="text-muted-foreground ms-1 text-xs">Resetuj hasło użytkownika, użytkownik otrzyma email z linkiem do zmiany hasła</p>
                        </div>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={() => {
                        setActiveUser(rowOrigin);
                        setOpenResetPassword(true);
                      }}
                    >
                      <div className="space-y-0.5">
                        <p>Resetuj hasło</p>
                        <p className="text-muted-foreground ms-1 text-xs">Resetuj hasło użytkownika, użytkownik otrzyma email z linkiem do zmiany hasła</p>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    []
  );

  const { table, setGlobalFilter } = useTanstackTable({
    name: 'users',
    columns,
    data: managedUsers,
    getRowId: (row) => String(row.id),
    rowSelection: rowSelection,
    onRowSelectionChange: setRowsSelection,
    defaultColumnVisibility: {
      phone: false,
      birthdate: false,
      last_login: false,
      active: false,
      createdAt: false,
    },
  });

  const { handleRowClick } = useTableRowSelection({
    table,
    setRowSelection: setRowsSelection,
  });

  const selectedUsers = useMemo<UserPrivate[]>(() => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) return [];
    const idSet = new Set(selectedIds.map(Number));
    return managedUsers.filter((user) => idSet.has(user.id));
  }, [rowSelection, managedUsers]);

  const addGroup = useMemo(() => {
    if (breakpoint) {
      return [
        {
          id: 1,
          label: 'Dodaj uż.',
          icon: <PlusIcon className={'size-4'} />,
          func: () => setOpenUserAdd(true),
        },
        {
          id: 2,
          label: 'Dodaj uży.',
          icon: <ListPlusIcon className={'size-4'} />,
          func: () => navigate('/manage/users/massImport'),
        },
      ];
    }
    return [
      {
        id: 1,
        label: 'Dodaj uż.',
        icon: <PlusIcon className={'size-4'} />,
        func: () => setOpenUserAdd(true),
      },
    ];
  }, [breakpoint, navigate]);

  return (
    <>
      <CreateUser open={openUserAdd} onOpenChange={setOpenUserAdd} parents={parents} />
      <EditUser
        user={activeUser || selectedUsers?.[0]}
        open={openEdit}
        onOpenChange={(open) => {
          setOpenEdit(open);
          if (!open) setActiveUser(undefined);
        }}
        students={students}
        parents={parents}
      />
      <DeleteUserDialog
        users={activeUser ? [activeUser] : selectedUsers}
        open={openDelete}
        onOpenChange={(open) => {
          setOpenDelete(open);
          if (!open) setActiveUser(undefined);
        }}
      />
      <ActivateUser
        user={activeUser || selectedUsers?.[0]}
        open={openChangeActive}
        onOpenChange={(open) => {
          setOpenChangeActive(open);
          if (!open) setActiveUser(undefined);
        }}
      />
      <ResetPassword
        user={activeUser || selectedUsers?.[0]}
        open={openResetPassword}
        onOpenChange={(open) => {
          setOpenResetPassword(open);
          if (!open) setActiveUser(undefined);
        }}
      />
      {/* Row-scoped only, like the report below: the menu item is only offered on pending rows. */}
      <ResendInvite
        user={activeUser}
        open={openResendInvite}
        onOpenChange={(open) => {
          setOpenResendInvite(open);
          if (!open) setActiveUser(undefined);
        }}
      />
      {/* Row-scoped only: the report is per student, so it deliberately does
          not fall back to the multi-row selection like the dialogs above. */}
      <GenerateStudentReport
        student={activeUser}
        open={openReport}
        onOpenChange={(open) => {
          setOpenReport(open);
          if (!open) setActiveUser(undefined);
        }}
      />
      <div className={'base-direction-col h-full'}>
        <div className={'viewport-height flex w-full flex-col gap-4'}>
          <TanstackTable
            table={table}
            label={'Użytkownicy'}
            isLoading={isPending}
            onGlobalFilterChange={setGlobalFilter}
            onRemoveSelectedRows={() => setOpenDelete(true)}
            onRowClick={handleRowClick}
            filterBy={[
              {
                id: 'lvl',
                title: 'Stanowisko',
                type: 'faceted',
                options: [
                  {
                    value: 0,
                    label: 'Uczeń',
                  },
                  {
                    value: 1,
                    label: 'Nauczyciel',
                  },
                  {
                    value: 2,
                    label: 'Administrator',
                  },
                ],
              },
              {
                id: 'active',
                title: 'Aktywność',
                type: 'faceted',
                options: [
                  {
                    label: 'Aktywny',
                    value: true,
                  },
                  {
                    label: 'Nieaktywny',
                    value: false,
                  },
                ],
              },
              {
                id: 'last_login',
                title: 'Rejestracja',
                type: 'faceted',
                options: [
                  {
                    label: 'Zarejestrowany',
                    value: true,
                  },
                  {
                    label: 'Niezarejestrowany',
                    value: false,
                  },
                ],
              },
              {
                id: 'birthdate',
                title: 'Data urodzenia',
                type: 'dateRange',
              },
              {
                id: 'createdAt',
                title: 'Data utworzenia',
                type: 'dateRange',
              },
            ]}
            addGroup={addGroup}
          />
        </div>
      </div>
    </>
  );
}
